import { useState } from 'react';
import { useTranslation } from '../../i18n/I18nProvider';
import { Button, Input, Modal, Select, Textarea } from '../../ui';

export interface GrantAccessValues {
  productId: string;
  activeUntil: string | null;
  note: string;
}

export interface GrantAccessModalProps {
  open: boolean;
  onClose: () => void;
  /** Ime ili e-mail klijenta — dijalog nikad ne smije biti anoniman. */
  clientLabel: string;
  products: readonly { id: string; name: string }[];
  loading?: boolean;
  onSubmit: (values: GrantAccessValues) => void;
}

/**
 * Ručno odobrenje pristupa.
 *
 * Bilješka je obavezna i to nije formalnost: ovim se otvara plaćeni sadržaj bez
 * traga u Stripeu, pa je jedini zapis zašto — ono što ovdje piše. Ide u
 * `audit_log` i u sam entitlement.
 */
export function GrantAccessModal({
  open,
  onClose,
  clientLabel,
  products,
  loading = false,
  onSubmit,
}: GrantAccessModalProps) {
  const { t } = useTranslation();
  const [productId, setProductId] = useState(products[0]?.id ?? '');
  const [until, setUntil] = useState('');
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | undefined>(undefined);

  function submit(): void {
    if (!productId) {
      setError(t('admin.grantProduct'));
      return;
    }
    if (note.trim().length < 3) {
      setError(t('admin.grantNoteHelp'));
      return;
    }
    setError(undefined);
    onSubmit({
      productId,
      // Datum bez vremena znači „do kraja tog dana", ne „do ponoći prije njega".
      activeUntil: until ? new Date(`${until}T23:59:59`).toISOString() : null,
      note: note.trim(),
    });
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('admin.grantTitle')}
      closeLabel={t('common.close')}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button loading={loading} onClick={submit}>
            {t('admin.grantAccess')}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <p className="text-[14px] leading-relaxed text-ink-muted">{t('admin.grantHelp')}</p>
        <p className="text-[14.5px] font-semibold text-ink">{clientLabel}</p>

        <Select
          id="grant-product"
          label={t('admin.grantProduct')}
          value={productId}
          onChange={(e) => setProductId(e.target.value)}
          options={products.map((p) => ({ value: p.id, label: p.name }))}
        />

        <Input
          id="grant-until"
          type="date"
          label={t('admin.grantUntil')}
          hint={t('admin.grantUntilHelp')}
          value={until}
          onChange={(e) => setUntil(e.target.value)}
        />

        <Textarea
          id="grant-note"
          label={t('admin.grantNote')}
          hint={t('admin.grantNoteHelp')}
          rows={3}
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />

        {error && (
          <p role="alert" className="text-[13.5px] text-danger">
            {error}
          </p>
        )}
      </div>
    </Modal>
  );
}
